import type { ResolvedToKeep } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { formatNumber } from "@/utils/tools";
import {
	Chip,
	type ChipVariant,
	Icon,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
	Typography,
} from "@st-marketplace/ui";

interface KeepValueCellProps {
	resolved: ResolvedToKeep | null;
}

type SourceType = ResolvedToKeep["type"];

const KeepValueCell: React.FC<KeepValueCellProps> = ({ resolved }) => {
	const { t } = useTranslation();

	if (!resolved) {
		return <Typography variant="body-sm">-</Typography>;
	}

	// Each source gets its own colour and label so the origin of the
	// value is obvious at a glance without needing the tooltip.
	const sourceConfig: Record<
		SourceType,
		{ label: string; tooltip: string; variant: ChipVariant }
	> = {
		SENDER: {
			label: t("sender.resolvedToKeepLabelSender"),
			tooltip: t("sender.resolvedToKeepTooltipSender"),
			variant: "info",
		},
		SENDER_GROUP: {
			label: t("sender.resolvedToKeepLabelGroup"),
			tooltip: t("sender.resolvedToKeepTooltipGroup"),
			variant: "info",
		},
		GLOBAL: {
			label: t("sender.resolvedToKeepLabelGlobal"),
			tooltip: t("sender.resolvedToKeepTooltipGlobal"),
			variant: "muted",
		},
	};

	const config = sourceConfig[resolved.type];

	return (
		<span className="inline-flex items-center gap-1">
			{/* Fixed-width, right-aligned number so every badge starts at the
			    same x position and the badges line up in a clean column. */}
			<Typography
				variant="body-sm"
				className="shrink-0 text-right tabular-nums"
			>
				{formatNumber(resolved.value)}
			</Typography>
			<Tooltip>
				<TooltipTrigger asChild>
					<span className="inline-flex cursor-help">
						<Chip variant={config.variant} size="sm">
							<span className="inline-flex items-center gap-0.5">
								{config.label}
								{/* Trailing info glyph makes it obvious the badge is
								    hoverable for an explanation. */}
								<Icon
									name="information-line"
									size="xs"
									className="opacity-70"
								/>
							</span>
						</Chip>
					</span>
				</TooltipTrigger>
				<TooltipContent>
					<p>{config.tooltip}</p>
					<p>{t("sender.resolvedToKeepTooltipHint")}</p>
				</TooltipContent>
			</Tooltip>
		</span>
	);
};

export default KeepValueCell;
